<template>
  <!-- eslint-disable vuejs-accessibility/mouse-events-have-key-events -->
  <div
    ref="menuRef"
    class="s-messageContextMenu">
    <portal to="modal">
      <transition name="fade">
        <div
          v-if="showEmojiPicker"
          ref="emojiPickerRef"
          class="s-messageContextMenu__emojiPicker s-messageContextMenu__item"
          :style="emojiPickerStyle"
          @click.stop>
          <Picker
            emoji="smile"
            title=""
            :i18n="emojiPickerTranslations"
            :custom="customEmojis"
            @select="handleEmojiSelect" />
        </div>
      </transition>
    </portal>
    <button
      v-for="emoji in quickReactionEmojis"
      :key="emoji"
      type="button"
      class="o-unstyled s-messageContextMenu__quickReactionBtn s-messageContextMenu__item"
      :class="{ '-disabled': hasReachedReactionLimit && !hasUserReactedWithEmoji(message, emoji) }"
      :disabled="hasReachedReactionLimit && !hasUserReactedWithEmoji(message, emoji)"
      :title="$t('chat.reactions.add')"
      @click.stop="handleQuickReaction(emoji)">
      {{ emoji }}
    </button>
    <span class="s-messageContextMenu__separator" />
    <button
      ref="emojiPickerBtnRef"
      type="button"
      class="o-unstyled s-messageContextMenu__quickReactionBtn s-messageContextMenu__item -picker"
      :class="{ '-disabled': hasReachedReactionLimit }"
      :disabled="hasReachedReactionLimit"
      :title="$t('chat.reactions.picker_title')"
      @click.stop="toggleEmojiPicker">
      <SvgIcon
        tiny
        type="chat_emoji" />
    </button>
    <button
      v-if="canReply"
      type="button"
      class="o-unstyled s-messageContextMenu__item"
      @click.stop="handleReply">
      <SvgIcon
        small
        type="reply" />
    </button>
    <template v-if="isOwnMessage">
      <span
        v-if="canReply"
        class="s-messageContextMenu__separator" />
      <button
        ref="triggerRef"
        type="button"
        class="o-unstyled s-messageContextMenu__item"
        :class="{ '-visible': isMenuOpen }"
        @click.stop="toggleMenu">
        <SvgIcon
          small
          type="dots" />
      </button>
    </template>
    <portal to="modal">
      <transition name="fade">
        <div
          v-if="isMenuOpen"
          ref="dropdownRef"
          class="s-messageContextMenu__dropdown"
          :style="dropdownStyle"
          @click.stop>
          <button
            v-if="canDelete"
            type="button"
            class="o-unstyled s-messageContextMenu__dropdownItem -delete"
            @click.stop="handleDelete">
            <SvgIcon
              small
              type="trash_can" />
            <span class="t-caption1">{{ $t('chat.message_context_menu.delete') }}</span>
          </button>
        </div>
      </transition>
    </portal>
  </div>
</template>

<script setup>
import { useTextDirection } from '@vueuse/core'
import {
  ref, computed, onMounted, onUnmounted, nextTick, defineAsyncComponent, watch,
} from 'vue'

import api from '@/logic/api'
import { getEmojiMartCustom } from '@/logic/emoji/customEmojis'
import { useChatStore } from '@/modules/chat/chatStore'
import { useChatThreadStore } from '@/modules/chat/chatThreadStore'
import {
  addReaction,
  hasUserReactedWithEmoji,
  addReactionToBackend,
  removeReactionFromBackend,
  parseReactions,
  canUserAddReaction,
} from '@/modules/chat/logic/reactions'

const Picker = defineAsyncComponent(async () => (await import('emoji-mart-vue')).Picker)

const props = defineProps({
  message: {
    type: Object,
    required: true,
  },
  canDelete: {
    type: Boolean,
    default: false,
  },
  showToolbar: {
    type: Boolean,
    default: true,
  },
  canReply: {
    type: Boolean,
    default: true,
  },
  isOwnMessage: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['delete', 'reply'])

const chatStore = useChatStore()

const textDirection = useTextDirection()
const menuRef = ref(null)
const toolbarRef = ref(null)
const triggerRef = ref(null)
const dropdownRef = ref(null)
const isMenuOpen = ref(false)
const isToolbarHovered = ref(false)
const shouldShowToolbar = ref(false)
let hideToolbarTimeout = null
const toolbarPosition = ref({ top: 0, left: 0 })
const dropdownPosition = ref({ top: 0, left: 0 })

// Emoji picker state
const emojiPickerBtnRef = ref(null)
const emojiPickerRef = ref(null)
const emojiPickerPosition = ref({ top: 0, left: 0 })

// Emoji picker is open if this message's ID matches the store's openEmojiPickerMessageId
const showEmojiPicker = computed(() => {
  return chatStore.openEmojiPickerMessageId !== null
         && chatStore.openEmojiPickerMessageId === props.message.id
})
const customEmojis = getEmojiMartCustom()

const emojiPickerTranslations = computed(() => ({
  search: api.i18n.t('emojiPicker.search'),
  notfound: api.i18n.t('emojiPicker.notfound'),
  categories: {
    search: api.i18n.t('emojiPicker.categories.search'),
    recent: api.i18n.t('emojiPicker.categories.recent'),
    people: api.i18n.t('emojiPicker.categories.people'),
    nature: api.i18n.t('emojiPicker.categories.nature'),
    foods: api.i18n.t('emojiPicker.categories.foods'),
    activity: api.i18n.t('emojiPicker.categories.activity'),
    places: api.i18n.t('emojiPicker.categories.places'),
    objects: api.i18n.t('emojiPicker.categories.objects'),
    symbols: api.i18n.t('emojiPicker.categories.symbols'),
    flags: api.i18n.t('emojiPicker.categories.flags'),
    custom: api.i18n.t('emojiPicker.categories.custom'),
  },
}))

function updateToolbarPosition() {
  if (menuRef.value) {
    // Get the parent message element for positioning
    const messageEl = menuRef.value.closest('.c-chat__message')
    if (!messageEl) {
      return
    }

    const rect = messageEl.getBoundingClientRect()
    const isRtl = textDirection.value === 'rtl'

    toolbarPosition.value = {
      top: rect.top - 40, // Position above the message with slight overlap
      left: isRtl ? rect.left : rect.right,
      transform: isRtl ? 'translateX(0)' : 'translateX(-100%)',
    }
  }
}

// Update toolbar visibility with delay to allow mouse transition
watch(() => props.showToolbar, (newVal) => {
  if (newVal) {
    // Show immediately
    if (hideToolbarTimeout) {
      clearTimeout(hideToolbarTimeout)
      hideToolbarTimeout = null
    }
    shouldShowToolbar.value = true
    nextTick(() => {
      updateToolbarPosition()
    })
  } else {
    // Hide with delay to allow mouse to reach toolbar
    if (hideToolbarTimeout) {
      clearTimeout(hideToolbarTimeout)
      hideToolbarTimeout = null
    }
    hideToolbarTimeout = setTimeout(() => {
      hideToolbarTimeout = null
      if (!isToolbarHovered.value && !isMenuOpen.value && !showEmojiPicker.value) {
        shouldShowToolbar.value = false
      }
    }, 150)
  }
}, { immediate: true })

// Keep toolbar visible while hovered
watch(isToolbarHovered, (newVal) => {
  if (newVal) {
    if (hideToolbarTimeout) {
      clearTimeout(hideToolbarTimeout)
      hideToolbarTimeout = null
    }
    shouldShowToolbar.value = true
  }
})

const emojiPickerStyle = computed(() => ({
  position: 'fixed',
  top: `${emojiPickerPosition.value.top}px`,
  left: `${emojiPickerPosition.value.left}px`,
  zIndex: 10001,
}))

function updateEmojiPickerPosition() {
  if (emojiPickerBtnRef.value) {
    const rect = emojiPickerBtnRef.value.getBoundingClientRect()
    const pickerWidth = 338 // emoji-mart default width
    const pickerHeight = 400 // approximate height

    // Position above the button by default
    let top = rect.top - pickerHeight - 8
    let left = rect.left - (pickerWidth / 2) + (rect.width / 2)

    // Keep within viewport
    if (top < 10) {
      top = rect.bottom + 8
    }
    if (left < 10) {
      left = 10
    }
    if (left + pickerWidth > window.innerWidth - 10) {
      left = window.innerWidth - pickerWidth - 10
    }

    emojiPickerPosition.value = { top, left }
  }
}

function toggleEmojiPicker() {
  if (showEmojiPicker.value) {
    // Close this picker
    chatStore.openEmojiPickerMessageId = null
  } else {
    // Open this picker (automatically closes any other)
    chatStore.openEmojiPickerMessageId = props.message.id
    nextTick(() => {
      updateEmojiPickerPosition()
    })
  }
}

function closeEmojiPicker() {
  if (showEmojiPicker.value) {
    chatStore.openEmojiPickerMessageId = null
  }
}

/**
 * Get the 4 quick reaction emojis from store
 */
const quickReactionEmojis = computed(() => chatStore.quickReactionEmojis)

/**
 * Check if user has reached the 5-reaction limit on this message
 */
const hasReachedReactionLimit = computed(() => {
  return !canUserAddReaction(props.message)
})

/**
 * Handle click on a quick reaction emoji
 */
async function handleQuickReaction(emoji) {
  const chatThreadStore = useChatThreadStore(chatStore.chatThread.azure_chat_thread_id)
  const currentReactions = parseReactions(props.message)

  // Check if user already reacted with this emoji (toggle behavior)
  const isToggleOff = hasUserReactedWithEmoji(props.message, emoji)

  const newReactions = addReaction(props.message, emoji)

  // Skip if reactions didn't change (e.g., limit reached)
  if (JSON.stringify(newReactions) === JSON.stringify(currentReactions)) {
    return
  }

  // Optimistic update
  chatThreadStore.updateMessageByAzureId(props.message.id.toString(), {
    metadata: {
      ...props.message.metadata,
      reactions: JSON.stringify(newReactions),
    },
  })

  // Update recent emojis (only when adding, not removing)
  if (!isToggleOff) {
    chatStore.updateRecentReactionEmojis(emoji)
  }

  // Save to backend
  let success
  if (isToggleOff) {
    success = await removeReactionFromBackend(
      chatStore.chatThread.id,
      props.message.id.toString(),
      emoji,
    )
  } else {
    success = await addReactionToBackend(
      chatStore.chatThread.id,
      props.message.id.toString(),
      emoji,
    )
  }

  // Rollback if failed
  if (!success) {
    chatThreadStore.updateMessageByAzureId(props.message.id.toString(), {
      metadata: {
        ...props.message.metadata,
        reactions: JSON.stringify(currentReactions),
      },
    })
  }
}

/**
 * Handle emoji selection from full picker
 */
async function handleEmojiSelect(emoji) {
  const selectedEmoji = emoji.native || emoji.colons || emoji.id
  try {
    await handleQuickReaction(selectedEmoji)
  } finally {
    closeEmojiPicker()
  }
}

const dropdownStyle = computed(() => ({
  position: 'fixed',
  top: `${dropdownPosition.value.top}px`,
  left: `${dropdownPosition.value.left}px`,
}))

// We update the position programatically, because the container has overflow: hidden, and  it will overlap with ChatMessageActions for the last message
function updateDropdownPosition() {
  if (triggerRef.value) {
    const rect = triggerRef.value.getBoundingClientRect()
    const isRtl = textDirection.value === 'rtl'
    const dropdownWidth = 160 // min-width of dropdown

    dropdownPosition.value = {
      top: rect.bottom + 2, // 2 just to let the dropdown be a little bit away from the trigger
      left: isRtl ? rect.left : rect.right - dropdownWidth,
    }
  }
}

function toggleMenu() {
  isMenuOpen.value = !isMenuOpen.value
  if (isMenuOpen.value) {
    nextTick(() => {
      updateDropdownPosition()
    })
  }
}

function closeMenu() {
  isMenuOpen.value = false
}

function handleReply() {
  emit('reply')
  closeMenu()
}

function handleDelete() {
  emit('delete')
  closeMenu()
}

function handleClickOutside(event) {
  const isOutsideMenu = menuRef.value && !menuRef.value.contains(event.target)
  const isOutsideToolbar = !toolbarRef.value || !toolbarRef.value.contains(event.target)
  const isOutsideDropdown = !dropdownRef.value || !dropdownRef.value.contains(event.target)

  if (isOutsideMenu && isOutsideToolbar && isOutsideDropdown) {
    closeMenu()
    // Also hide toolbar when clicking outside
    shouldShowToolbar.value = false
    isToolbarHovered.value = false
  }

  // Close emoji picker if clicking outside of it
  if (showEmojiPicker.value) {
    const isInsideEmojiPicker = emojiPickerRef.value && emojiPickerRef.value.contains(event.target)
    const isInsidePickerButton = emojiPickerBtnRef.value && emojiPickerBtnRef.value.contains(event.target)

    if (!isInsideEmojiPicker && !isInsidePickerButton) {
      closeEmojiPicker()
    }
  }
}

function handleKeyDown(event) {
  if (event.key === 'Escape') {
    if (showEmojiPicker.value) {
      closeEmojiPicker()
    }
    if (isMenuOpen.value) {
      closeMenu()
    }
  }
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
  document.addEventListener('keydown', handleKeyDown)
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
  document.removeEventListener('keydown', handleKeyDown)

  if (hideToolbarTimeout) {
    clearTimeout(hideToolbarTimeout)
    hideToolbarTimeout = null
  }
})

defineExpose({
  toggleMenu,
  closeMenu,
  isMenuOpen,
  isToolbarHovered,
})
</script>

<style scoped lang="scss">
@import '@/assets/scss/config';

// Note: .s-messageContextMenu positioning is handled in _chat.scss for proper parent context

.s-messageContextMenu__quickReactionBtn {
  &:active {
    transform: scale(0.95);
  }

  &.-picker,
  &.-more {
    .c-icon {
      color: $color-dark_medium_emphasis;
    }
  }

  &.-disabled,
  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
    pointer-events: none;
  }
}

.s-messageContextMenu__emojiPicker {
  background: $color-white;
  border-radius: $spacing-2;
  box-shadow: 0px 4px 16px 0px rgba(8, 8, 8, 0.16);
}
</style>
